import React, { type JSX } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { NavigationBar, PageLayout, Stack, Text } from "@fidelix/fx-miranda";

import { EntityService } from "../services/entity-service";
import {
  AuditLogEntity,
  IActiveSection,
  ActiveSection,
  isPathIncluded,
} from "../helper";
import { Entity } from "../dtos/entity.model";
import Breadcrumb from "../breadcrumbs/breadcrumb";

export type NavItem = {
  id: string;
  label: string;
  queryParams?: Record<string, string>;
};

export type getNavInfo = () => void | {
  label?: string;
  navList: NavItem[];
};

const WithSideNavi = (
  Component: React.FC<IActiveSection>,
  getNavInfo: getNavInfo = () => {},
) => {
  const MyComp = (
    prop: Record<
      string,
      string | number | JSX.Element | boolean | EntityService<Entity>
    >,
  ) => {
    const params = useParams();
    const activeSection = params["*"] as AuditLogEntity | ActiveSection;
    const navigate = useNavigate();
    const { label, navList } = getNavInfo() || { label: null, navList: [] };
    const isRouteExists = navList.length > 0;
    const breadcrumbPaths = ["/edit", "/new"];

    const handleSideNavigation = (navItem: NavItem) => {
      const { id: tab, queryParams = {} } = navItem;
      const pathname = window.location.pathname.replace(params["*"], tab);

      if (queryParams && Object.keys(queryParams).length > 0) {
        const nextSearchParams = new URLSearchParams();
        Object.entries(queryParams).forEach(([key, value]) => {
          nextSearchParams.set(key, value);
        });
        navigate({
          pathname,
          search: `?${nextSearchParams.toString()}`,
        });
        return;
      }

      navigate(pathname);
    };

    return (
      <Stack>
        <SideNavContainer axis="y" isRouteExists={isRouteExists}>
          {isRouteExists && (
            <>
              {label && <InfoTitle variant={"title2Bold"}>{label}</InfoTitle>}
              <NavigationBar>
                {navList.map((navItem: NavItem) => (
                  <NavigationBar.Item
                    key={navItem.id}
                    label={navItem.label}
                    isActive={params["*"] === navItem.id}
                    onPress={() => handleSideNavigation(navItem)}
                  />
                ))}
              </NavigationBar>
            </>
          )}
        </SideNavContainer>

        <MainPanelLayout>
          <BreadcrumbWrapper axis="y" isRouteExists={isRouteExists}>
            {breadcrumbPaths.some(isPathIncluded) && <Breadcrumb />}

            <ComponentWrapper>
              <Component
                {...prop}
                activeSection={isRouteExists ? activeSection : undefined}
              />
            </ComponentWrapper>
          </BreadcrumbWrapper>
        </MainPanelLayout>
      </Stack>
    );
  };
  return MyComp;
};

export default WithSideNavi;

const MainPanelLayout = styled(PageLayout.MainPanel)`
  width: 100%;
  flex: unset;
  padding: 0;
  padding-right: ${({ theme }) => theme.spaceNumber("xxsmall")}%;
`;

const BreadcrumbWrapper = styled(Stack)<{ isRouteExists: boolean }>`
  padding-top: ${({ isRouteExists, theme }) =>
    isRouteExists ? 0 : theme.space("large")};
  width: 100%;
  margin-right: ${({ isRouteExists, theme }) =>
    isRouteExists
      ? theme.spaceNumber("xxsmall")
      : theme.spaceNumber("xsmall")}%;
`;

const ComponentWrapper = styled.div`
  margin-top: ${(p) => p.theme.space("large")};
  width: 100%;
  margin-bottom: ${(p) => p.theme.spaceNumber("xxsmall")}%;
`;

const InfoTitle = styled(Text)`
  margin: ${(p) => p.theme.space("none")} ${(p) => p.theme.space("none")}
    ${(p) => p.theme.space("normal")} ${(p) => p.theme.space("normal")};
`;

const SideNavContainer = styled(Stack)<{ isRouteExists: boolean }>`
  width: ${({ isRouteExists }) => (isRouteExists ? "20%" : "15%")};
  margin: ${(p) => p.theme.space("large")} ${(p) => p.theme.space("none")}
    ${(p) => p.theme.space("none")} ${(p) => p.theme.space("normal")};
`;
